import { type AskPunttAgentStep } from "@mg/schemas/src/christo/askPuntt";
import {
  Brain,
  CaretDown,
  CaretRight,
  CheckCircle,
  CircleNotch,
  Wrench,
} from "@phosphor-icons/react";
import { Flex, Text } from "@radix-ui/themes";
import { useState } from "react";

interface AgentStepsProps {
  steps: AskPunttAgentStep[];
  /** True while the agent is still working (no final content yet) */
  isStreaming: boolean;
}

function ThinkingStep({
  step,
}: {
  step: AskPunttAgentStep & { type: "thinking" };
}) {
  const preview =
    step.content.length > 120
      ? `${step.content.slice(0, 120)}...`
      : step.content;

  return (
    <Flex align="start" gap="2" className="px-2 py-1">
      <Brain size={14} className="mt-0.5 shrink-0 text-puntt-neutral-gray-9" />
      <Text size="2" className="italic text-puntt-neutral-gray-9">
        {preview}
      </Text>
    </Flex>
  );
}

function ToolCallStep({
  step,
}: {
  step: AskPunttAgentStep & { type: "tool_call" };
}) {
  const primaryName = step.statusName || step.displayName;
  const showOriginal = !!step.statusName;
  const secondaryText = step.description
    ? step.description
    : step.args
      ? JSON.stringify(step.args).slice(0, 80)
      : "";

  return (
    <Flex align="center" gap="2" className="px-2 py-1">
      <CircleNotch
        size={14}
        className="shrink-0 animate-spin text-puntt-accent-9"
      />
      <Text size="2" className="font-medium text-puntt-neutral-gray-11">
        {primaryName}
      </Text>
      {showOriginal && (
        <Text size="1" className="text-puntt-neutral-gray-8">
          {step.displayName}
        </Text>
      )}
      {secondaryText && (
        <Text size="2" className="truncate text-puntt-neutral-gray-9">
          {secondaryText}
        </Text>
      )}
    </Flex>
  );
}

function ToolResultStep({
  step,
}: {
  step: AskPunttAgentStep & { type: "tool_result" };
}) {
  const summary =
    step.summary.length > 100
      ? `${step.summary.slice(0, 100)}...`
      : step.summary;

  return (
    <Flex align="center" gap="2" className="px-2 py-1">
      <CheckCircle
        size={14}
        weight="fill"
        className="shrink-0 text-malachite-700"
      />
      <Text size="2" className="font-medium text-puntt-neutral-gray-11">
        {step.displayName}
      </Text>
      {step.resultCount !== undefined && step.resultCount > 0 && (
        <Text size="2" className="text-puntt-neutral-gray-9">
          {step.resultCount} {step.resultCount === 1 ? "result" : "results"}
        </Text>
      )}
      {summary && (
        <Text size="2" className="truncate text-puntt-neutral-gray-9">
          {summary}
        </Text>
      )}
    </Flex>
  );
}

export function AgentSteps({ steps, isStreaming }: AgentStepsProps) {
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);

  // Auto-expand while streaming, collapse after answer arrives
  const expanded = manualToggle !== null ? manualToggle : isStreaming;

  const stepCount = steps.length;
  const summaryText = isStreaming
    ? "Working..."
    : `${stepCount} step${stepCount === 1 ? "" : "s"}`;

  return (
    <div className="rounded-xl border border-puntt-neutral-gray-4/50 bg-base-white p-3 shadow-sm">
      <div className="space-y-1">
        {isStreaming ? (
          <Flex
            align="center"
            gap="2"
            className="mb-3 border-b border-puntt-neutral-gray-4 pb-2"
          >
            <CircleNotch
              size={16}
              className="animate-spin text-puntt-accent-9"
            />
            <Text
              size="2"
              weight="medium"
              className="text-puntt-neutral-gray-12"
            >
              Working
            </Text>
          </Flex>
        ) : (
          <button
            onClick={() => setManualToggle(!expanded)}
            className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-left transition-colors hover:bg-puntt-neutral-gray-3"
          >
            <Wrench size={12} className="text-puntt-neutral-gray-9" />
            <Text size="1" className="font-medium text-puntt-neutral-gray-11">
              {summaryText}
            </Text>
            {expanded ? (
              <CaretDown size={10} className="text-puntt-neutral-gray-9" />
            ) : (
              <CaretRight size={10} className="text-puntt-neutral-gray-9" />
            )}
          </button>
        )}

        {expanded && (
          <div className="ml-3 space-y-0.5 border-l-2 border-puntt-accent-6/30 pl-3">
            {steps.map((step, idx) => {
              switch (step.type) {
                case "thinking":
                  return <ThinkingStep key={idx} step={step} />;
                case "tool_call":
                  return <ToolCallStep key={idx} step={step} />;
                case "tool_result":
                  return <ToolResultStep key={idx} step={step} />;
                default:
                  return null;
              }
            })}
          </div>
        )}
      </div>
    </div>
  );
}
